import type { Coupon } from '@/entities/coupons/coupon.entity';
import type { Payment } from '@/entities/payments/payment.entity';
import type { PaginatedResponse } from '@/entities/pagination/paginated-response';
import { Service } from './http-service';
import {
  CouponType,
  type ApplyCouponDto,
  type CouponBenefitsDto,
  type CouponGeneratedRevenueDto,
  type CouponValidationResponseDto,
  type CreateLegacyCouponPaymentDto,
} from '@carvia/ros-client-types';
import type { BookingStatus } from '@/entities/bookings/booking-status.enum';

export interface FindCouponGeneratedRevenueQueryDto {
  createdDateAfter?: string;
  createdDateBefore?: string;
  status?: BookingStatus[];
}

export interface CreateCouponDto {
  remainingValue?: number;
  originalValue?: number;
  isActive?: boolean;
  type: CouponType;
  code?: string;
  benefits?: CouponBenefitsDto;
  validTo?: Date | null;
  validFrom?: Date | null;
  oneRedemptionPerCustomer?: boolean;
  maxRedemptions?: number | null;
  customerData?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    street?: string;
    city?: string;
    zip?: string;
    country?: string;
    email?: string;
  };
}

export interface UpdateCouponDto {
  remainingValue?: number;
  originalValue?: number;
  isActive?: boolean;
  type?: CouponType;
  code?: string;
  benefits?: CouponBenefitsDto | null;
  validTo?: Date | null;
  validFrom?: Date | null;
  oneRedemptionPerCustomer?: boolean;
  maxRedemptions?: number | null;
  customerData?: CreateCouponDto['customerData'];
}

export interface FindAllCouponsQueryDto {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  type?: CouponType;
}

class CouponsService extends Service<Coupon, CreateCouponDto, UpdateCouponDto> {
  async getAllWithQuery(params: FindAllCouponsQueryDto = {}) {
    const res = await this.httpClient.get<PaginatedResponse<Coupon>>(`${this.endpoint}`, {
      params,
    });
    return res.data;
  }

  async downloadPdf(id: string): Promise<Blob> {
    const response = await this.httpClient.get(`${this.endpoint}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getGeneratedRevenue(id: string, params: FindCouponGeneratedRevenueQueryDto = {}) {
    const res = await this.httpClient.get<CouponGeneratedRevenueDto>(
      `${this.endpoint}/${id}/revenue`,
      { params },
    );
    return res.data;
  }

  async update(id: string, data: UpdateCouponDto): Promise<Coupon> {
    const response = await this.httpClient.patch<Coupon>(`${this.endpoint}/${id}`, data);
    return response.data;
  }

  async applyCoupon(dto: ApplyCouponDto): Promise<CouponValidationResponseDto> {
    const response = await this.httpClient.post<CouponValidationResponseDto>(
      `${this.endpoint}/apply`,
      dto,
    );
    return response.data;
  }

  async removeCoupon(dto: { bookingId: string; couponId: string }): Promise<{ success: true }> {
    const response = await this.httpClient.post<{ success: true }>(`${this.endpoint}/remove`, dto);
    return response.data;
  }

  async createLegacyCouponPayment(dto: CreateLegacyCouponPaymentDto): Promise<Payment> {
    const response = await this.httpClient.post<Payment>(`${this.endpoint}/legacy-coupon`, dto);
    return response.data;
  }
}

export const couponsService = new CouponsService('/coupons');
